if (!customElements.get('knox-help-studio')) {
  customElements.define('knox-help-studio', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = this.querySelector('form');
      this.status = this.querySelector('[role="status"], [role="alert"]');
      if (!this.form) return;
      this.addEventListener('knox:help-mode', event => {
        this.setMode(event.detail?.mode);
      }, { signal });
      this.need = this.form.elements.namedItem('contact[Help needed]');
      this.focusField = this.form.elements.namedItem('contact[Focus]');
      this.goal = this.form.elements.namedItem('contact[Goal]');
      this.focusControls = [...this.querySelectorAll('[data-help-focus]')];
      this.goalControls = [...this.querySelectorAll('[data-help-goal]')];
      this.body = this.form.elements.namedItem('contact[body]');
      this.submitButton = this.querySelector('.khs-submit');
      if (!this.need || !this.body || !this.submitButton) return;
      this.defaultNeed ||= this.need.value === 'Supplier registration' ? 'Choosing products' : this.need.value;
      this.form.noValidate = true;
      this.submitButton.type = 'button';
      this.dataset.enhanced = 'true';
      // Validate before Shopify's captcha listener can submit an incomplete enquiry.
      this.addEventListener('click', event => {
        if (!event.target.closest('.khs-submit')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const invalid = this.firstInvalid();
        if (invalid) this.revealInvalid(invalid);
        else this.form.requestSubmit();
      }, { capture: true, signal });
      this.form.addEventListener('submit', event => {
        const invalid = this.firstInvalid();
        if (!invalid) return;
        event.preventDefault();
        this.revealInvalid(invalid);
      }, { signal });
      this.form.addEventListener('input', event => {
        if (event.target === this.errorControl && event.target.validity.valid) this.clearError();
      }, { signal });
      this.need.addEventListener('change', () => {
        if (this.need.value !== 'Supplier registration') this.defaultNeed = this.need.value;
        this.updateMode();
      }, { signal });
      this.focusControls.forEach(control => control.addEventListener('change', () => this.updateCategories(), { signal }));
      this.goalControls.forEach(control => control.addEventListener('change', () => {
        const goalNeed = { 'Try devices': 'Device trial', 'Deploy a solution': 'Deployment', 'Build a system': 'Choosing products', 'Help me choose': 'Choosing products' };
        if (['Choosing products', 'Deployment', 'Device trial'].includes(this.need.value)) {
          this.need.value = goalNeed[this.goal.value] || 'Choosing products';
          this.defaultNeed = this.need.value;
        }
        this.updateMode();
      }, { signal }));
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-category-label], [name="contact[Timeline]"]')) this.updateSummary();
      }, { signal });
      this.form.addEventListener('input', event => {
        if (event.target.name === 'contact[Scale]') this.updateSummary();
      }, { signal });
      this.form.addEventListener('reset', () => queueMicrotask(() => {
        this.clearError();
        this.updateMode();
      }), { signal });
      this.updateMode();
      this.card = this.querySelector('[data-help-request-card]');
      this.cardObserver?.disconnect();
      if (this.card && 'ResizeObserver' in window) {
        this.cardObserver = new ResizeObserver(() => this.updateFloatingCard());
        this.cardObserver.observe(this.card);
      }
      window.addEventListener('resize', () => this.updateFloatingCard(), { signal });
      this.updateFloatingCard();
      // Reopen saved optional requirements after a native server validation error.
      if (this.dataset.hasResult === 'true') {
        const details = this.querySelector('.khs-requirements');
        if (details && [...details.querySelectorAll('input, select, textarea')].some(control => control.type === 'checkbox' ? control.checked : Boolean(control.value))) details.open = true;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.cardObserver?.disconnect();
    }

    // Explorer tab changes retain the buyer's fields, category choices and open details.
    setMode(mode) {
      if (!this.need) return;
      if (mode === 'procurement') {
        if (this.need.value !== 'Supplier registration') this.defaultNeed = this.need.value;
        this.need.value = 'Supplier registration';
      } else if (['help', 'build', 'deploy', 'try'].includes(mode)) {
        if (this.need.value === 'Supplier registration') this.need.value = this.defaultNeed || 'Choosing products';
        if (mode !== 'help' && this.goal) {
          this.goal.value = { build: 'Build a system', deploy: 'Deploy a solution', try: 'Try devices' }[mode];
          this.goalControls.find(control => control.checked)?.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else return;
      this.clearError();
      this.updateMode();
    }

    setFocus(value) {
      if (!this.focusField || !this.focusControls.some(control => control.value === value)) return;
      this.focusField.value = value;
      this.updateCategories();
    }

    updateCategories() {
      const focus = this.focusField?.value || 'Not sure yet';
      const procurement = this.need.value === 'Supplier registration';
      const hint = this.querySelector('[data-category-hint]');
      if (hint) hint.hidden = focus !== 'Not sure yet';
      this.querySelectorAll('[data-category-domain]').forEach(group => {
        const visible = focus === 'Both' || focus === group.dataset.categoryDomain;
        group.hidden = !visible;
        group.querySelectorAll('input').forEach(input => { input.disabled = procurement || !visible; });
      });
      if (this.errorControl?.disabled) this.clearError();
      this.updateSummary();
    }

    updateSummary() {
      const summary = this.querySelector('[data-help-summary]');
      if (!summary || !this.goal || !this.focusField) return;
      const procurement = this.need.value === 'Supplier registration';
      const chosen = [...this.querySelectorAll('[data-category-label]:checked')].filter(control => !control.disabled);
      const count = this.querySelector('[data-category-count]');
      if (count) count.textContent = chosen.length ? chosen.length + ' selected · choose any' : 'Choose any that interest you';
      summary.replaceChildren();
      const title = document.createElement('p');
      title.className = 'khs-summary-title';
      title.textContent = procurement ? 'Supplier portal registration' : this.goal.value;
      const focus = document.createElement('p');
      focus.className = 'khs-summary-focus';
      const focusLabel = this.focusField.value === 'Both' ? 'XR + robotics' : this.focusField.value;
      // On the homepage the summary stays short: the goal plus one plain descriptor, no chips.
      const compact = !!this.closest('knox-home-explorer');
      focus.textContent = procurement ? 'We’ll help your procurement team with the registration process.' : (compact || focusLabel === 'Not sure yet') ? 'We’ll help you choose the right technology.' : focusLabel;
      summary.append(title, focus);
      if (!procurement && !compact) {
        const chips = document.createElement('div');
        chips.className = 'khs-summary-chips';
        const labels = chosen.slice(0, 4).map(control => control.dataset.categoryLabel);
        if (chosen.length > 4) labels.push('+' + (chosen.length - 4) + ' more');
        const scale = this.form.elements.namedItem('contact[Scale]')?.value.trim();
        const timeline = this.form.elements.namedItem('contact[Timeline]')?.value;
        if (scale) labels.push(scale);
        if (timeline) labels.push(timeline);
        labels.forEach(label => {
          const chip = document.createElement('span');
          chip.className = 'khs-summary-chip';
          chip.textContent = label;
          chips.append(chip);
        });
        if (labels.length) summary.append(chips);
      }
      summary.hidden = false;
      this.updateFloatingCard();
    }

    updateFloatingCard() {
      if (!this.card || !this.isConnected) return;
      const top = parseFloat(getComputedStyle(this.card).top) || 24;
      this.card.dataset.tooTall = String(this.card.getBoundingClientRect().height > window.innerHeight - top - 20);
    }

    updateMode() {
      const procurement = this.need.value === 'Supplier registration';
      this.dataset.mode = procurement ? 'procurement' : 'help';
      this.querySelectorAll('[data-setup-field]').forEach(field => {
        field.hidden = procurement;
        field.querySelectorAll('input, select, textarea').forEach(control => { control.disabled = procurement; });
      });
      this.querySelectorAll('[data-supplier-field]').forEach(label => {
        label.hidden = !procurement;
        label.querySelectorAll('input').forEach(input => { input.disabled = !procurement; });
      });
      this.updateCategories();
      this.querySelector('[data-help-request]').value = procurement ? 'Supplier portal registration' : 'Setup guidance';
      this.querySelector('[data-help-heading]').textContent = procurement ? 'Request supplier registration.' : 'Let’s find your setup.';
      this.querySelector('[data-help-intro]').textContent = procurement ? 'Share your organization, portal and registration requirements.' : 'Choose what matters to your project. We’ll help with products, compatibility and the next step.';
      this.querySelector('[data-help-message-label]').textContent = procurement ? 'Registration requirements' : 'What would you like to do?';
      this.body.placeholder = procurement ? 'Tell us which registration steps or documents are needed and any deadline. Do not include passwords or credentials.' : 'Describe your goal or question. You can add project details in the configuration fields, or leave those open for us to help with.';
      this.querySelector('[data-help-submit]').textContent = procurement ? 'Send registration request' : 'Send my request';
      const note = this.querySelector('[data-help-note]');
      const noteText = procurement ? 'Our team will confirm the registration requirements and next steps. ' : 'We’ll confirm availability, compatibility, trial options and pricing with you. ';
      // Retain the Liquid-rendered privacy link when changing the message.
      if (note.firstChild?.nodeType === Node.TEXT_NODE) note.firstChild.textContent = noteText;
      else note.prepend(document.createTextNode(noteText));
      if (this.errorControl?.disabled) this.clearError();
    }

    firstInvalid() {
      return [...this.form.elements].find(control => !control.disabled && control.willValidate && !control.validity.valid);
    }

    clearError() {
      if (this.errorControl) {
        this.errorControl.removeAttribute('aria-invalid');
        if (this.previousDescription) this.errorControl.setAttribute('aria-describedby', this.previousDescription);
        else this.errorControl.removeAttribute('aria-describedby');
      }
      this.errorMessage?.remove();
      this.errorControl = null;
      this.errorMessage = null;
      this.previousDescription = '';
    }

    revealInvalid(control) {
      this.clearError();
      this.errorControl = control;
      this.previousDescription = control.getAttribute('aria-describedby') || '';
      this.errorMessage = document.createElement('span');
      this.errorMessage.id = this.form.id + '-field-error';
      this.errorMessage.className = 'khs-field-error';
      this.errorMessage.setAttribute('role', 'alert');
      this.errorMessage.textContent = control.validationMessage;
      control.setAttribute('aria-invalid', 'true');
      control.setAttribute('aria-describedby', [this.previousDescription, this.errorMessage.id].filter(Boolean).join(' '));
      control.insertAdjacentElement('afterend', this.errorMessage);
      const details = control.closest('details');
      if (details) details.open = true;
      control.focus();
    }
  });
}